"use client";

import { useMemo, useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { createUniqueAnchor } from "@/lib/markdown-utils";

type Heading = {
  level: number;
  title: string;
  anchor: string;
  children: Heading[];
  id: string; // Уникальный идентификатор для React keys
};

function extractHeadings(markdown: string): Heading[] {
  const lines = markdown.split("\n");
  const result: Heading[] = [];
  let idCounter = 0;
  const seenAnchors = new Set<string>(); // Для предотвращения дубликатов

  for (const line of lines) {
    const m = line.match(/^(#{1,4})\s+(.+)$/);
    if (!m) continue;
    const level = m[1]!.length;
    const raw = m[2]!.trim();
    const anchor = createUniqueAnchor(raw);

    // Проверяем, не видели ли мы уже такой anchor
    if (seenAnchors.has(anchor)) {
      continue; // Пропускаем дубликат
    }

    seenAnchors.add(anchor);

    result.push({
      level,
      title: raw.replace(/[*`_~]/g, ""),
      anchor: anchor,
      children: [],
      id: `heading-${idCounter++}`,
    });
  }
  return result;
}

function buildHeadingTree(headings: Heading[]): Heading[] {
  const tree: Heading[] = [];
  const stack: Heading[] = [];
  let globalCounter = 0; // Глобальный счетчик для уникальных ID

  for (const heading of headings) {
    while (stack.length > 0 && stack[stack.length - 1].level >= heading.level) {
      stack.pop();
    }

    // Создаем новый объект с уникальным ID
    const newHeading: Heading = {
      ...heading,
      id: `heading-${globalCounter++}`, // Уникальный ID для каждого заголовка
      children: [],
    };

    if (stack.length === 0) {
      tree.push(newHeading);
    } else {
      stack[stack.length - 1].children.push(newHeading);
    }

    stack.push(newHeading);
  }

  return tree;
}

function TocItem({
  heading,
  level = 0,
  expandedItems,
  onToggle,
  onNavigate,
}: {
  heading: Heading;
  level?: number;
  expandedItems: Set<string>;
  onToggle: (id: string) => void;
  onNavigate: (anchor: string) => void;
}) {
  const hasChildren = heading.children.length > 0;
  const isExpanded = expandedItems.has(heading.id);
  const indentSize = 16;

  return (
    <div>
      <div
        className={`flex items-start gap-1 py-1 rounded-sm transition-colors hover:bg-accent/50`}
        style={{ paddingLeft: `${level * indentSize}px` }}
      >
        {/* Expand/collapse button */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle(heading.id);
            }}
            className="flex items-center justify-center w-5 h-5 hover:bg-accent rounded-sm transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
        ) : (
          <div className="w-5" />
        )}

        {/* Heading link */}
        <a
          href={`#${heading.anchor}`}
          onClick={(e) => {
            e.preventDefault();
            onNavigate(heading.anchor);
          }}
          className="flex-1 min-w-0 text-sm text-muted-foreground hover:text-foreground cursor-pointer break-words"
        >
          {heading.title}
        </a>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {heading.children.map((child) => (
            <TocItem
              key={child.id}
              heading={child}
              level={level + 1}
              expandedItems={expandedItems}
              onToggle={onToggle}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function TableOfContents({
  markdown,
  headingAnchors,
}: {
  markdown: string;
  headingAnchors?: Array<{ level: number; title: string; anchor: string }>;
}) {
  const headings = useMemo(() => {
    if (headingAnchors) {
      // Используем предварительно сгенерированные anchor'ы
      return headingAnchors.map((h, index) => ({
        level: h.level,
        title: h.title,
        anchor: h.anchor,
        children: [],
        id: `heading-${index}`,
      }));
    }
    // Fallback к старой логике, если headingAnchors не передан
    return extractHeadings(markdown);
  }, [markdown, headingAnchors]);

  const headingTree = useMemo(() => buildHeadingTree(headings), [headings]);

  // State for expanded items
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    // Auto-expand first and second levels by default
    headingTree.forEach((heading) => {
      if (heading.level === 1 || heading.level === 2) {
        initial.add(heading.id);
      }
    });
    return initial;
  });

  if (!headings.length) return null;

  const onNavigate = (anchor: string) => {
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onToggle = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <aside className="w-60 shrink-0 sticky top-8 self-start">
      <div className="p-0">
        <div className="text-xs font-medium text-muted-foreground mb-2">
          On this page
        </div>
        <nav className="relative">
          {headingTree.map((heading) => (
            <TocItem
              key={heading.id}
              heading={heading}
              expandedItems={expandedItems}
              onToggle={onToggle}
              onNavigate={onNavigate}
            />
          ))}
        </nav>
      </div>
    </aside>
  );
}
